import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { Button, Card, Row, Col, Badge, Alert } from 'react-bootstrap';
import homepageApi from '../../api/homepage';
import CustomInput from '../../common/FormComponents/Input';
import ImageInput from '../../common/FormComponents/ImageInput';
import CustomFieldArray from '../../common/FormComponents/CustomFieldArray';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import Loader from '../../common/UI/Loader';
import ThemeEditor from './ThemeEditor';

// For sections like: hero_right_section, why_tryageless, proud_section, caring_section
const SingleSectionEditor = ({ section, backToList, refreshSections }) => {
  const [loading, setLoading] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  // Define fields based on section type
  const getFieldsConfig = () => {
    switch (section.sectionKey) {
      case 'hero_right_section':
        return {
          title: true,
          subtitle: true,
          perfectFor: true,
          pricing: true,
          ctaText: true,
          ctaLink: true,
        };
      case 'why_tryageless':
        return {
          title: true,
          image: true,
          features: true,
          ctaText: true,
          ctaLink: true,
        };
      case 'proud_section':
        return {
          text: true,
          count: true,
          countLabel: true,
        };
      case 'caring_section':
        return {
          title: true,
          subtitle: true,
          ctaText: true,
          ctaLink: true,
        };
      default:
        return {};
    }
  };

  const getSectionIcon = () => {
    const icons = {
      hero_right_section: 'fa-align-right',
      why_tryageless: 'fa-star',
      proud_section: 'fa-trophy',
      caring_section: 'fa-heart',
    };
    return icons[section.sectionKey] || 'fa-file';
  };

  const getSectionDescription = () => {
    const descriptions = {
      hero_right_section:
        'Configure the hero section content displayed on the right side of your homepage',
      why_tryageless: 'Highlight key features and benefits of your service',
      proud_section: 'Display impressive statistics and achievements',
      caring_section: 'Show your commitment to customer care',
    };
    return descriptions[section.sectionKey] || 'Manage this section content';
  };

  const fields = getFieldsConfig();

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      let payload = {
        sectionKey: section.sectionKey,
        sectionName: section.sectionName,
        sectionType: 'single',
        data: {},
      };

      // Build data object based on fields
      Object.keys(fields).forEach((key) => {
        if (key === 'image' && values[key]?.[0]?.imgData) {
          // Will be handled separately for FormData
        } else if (
          (key === 'features' || key === 'perfectFor') &&
          Array.isArray(values[key])
        ) {
          payload.data[key] = values[key].filter((f) => f);
        } else if (key === 'pricing') {
          payload.data.pricing = {
            originalPrice: values.originalPrice || '',
            discountedPrice: values.discountedPrice || '',
            discountLabel: values.discountLabel || '',
            description: values.pricingDescription || '',
          };
        } else {
          payload.data[key] = values[key];
        }
      });

      // Handle image upload
      const hasFileUpload =
        fields.image &&
        values.image?.[0]?.imgData &&
        values.image[0].imgData instanceof File;

      if (hasFileUpload) {
        // Create FormData with all required fields
        const formData = new FormData();
        formData.append('sectionKey', payload.sectionKey);
        formData.append('sectionName', payload.sectionName);
        formData.append('sectionType', payload.sectionType);
        formData.append('data', JSON.stringify(payload.data));
        formData.append('file', values.image[0].imgData);
        formData.append('imageType', 'image');
        payload = formData;
      } else {
        if (section.data?.image) {
          payload.data.image = section.data.image;
        }
        // No file upload - send as JSON
      }

      await homepageApi.upsertHomepageSection(payload);
      toastSuccess('Section updated successfully');
      refreshSections();
    } catch (error) {
      toastError('Failed to update section');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader loading={loading} />;
  }

  return (
    <section className="content_wrapper product_add_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                <i className="fa fa-home me-2"></i>Landing Page CMS
              </li>
              <li className="active">{section.sectionName}</li>
            </ol>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              {/* Enhanced Header */}
              <div className="header_top border-bottom pb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className="mb-2">
                      <i
                        className={`fa ${getSectionIcon()} me-2 text-primary`}
                      ></i>
                      {section.sectionName}
                    </h3>
                    <p className="text-muted mb-2">{getSectionDescription()}</p>
                    <div className="d-flex gap-2 align-items-center">
                      <Badge bg="success" className="text-uppercase">
                        Single Section
                      </Badge>
                      <Badge bg="secondary" className="font-monospace">
                        {section.sectionKey}
                      </Badge>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>Theme
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={backToList}
                    >
                      <i className="fa fa-arrow-left me-2"></i>Back to Sections
                    </Button>
                  </div>
                </div>
              </div>

              <Formik
                initialValues={{
                  title: section.data?.title || '',
                  subtitle: section.data?.subtitle || '',
                  text: section.data?.text || '',
                  image: section.data?.image
                    ? [{ PrevUrl: section.data.image.location }]
                    : [],
                  features: section.data?.features || [''],
                  perfectFor: section.data?.perfectFor || [''],
                  ctaText: section.data?.ctaText || '',
                  ctaLink: section.data?.ctaLink || '',
                  count: section.data?.count || 0,
                  countLabel: section.data?.countLabel || '',
                  originalPrice: section.data?.pricing?.originalPrice || '',
                  discountedPrice: section.data?.pricing?.discountedPrice || '',
                  discountLabel: section.data?.pricing?.discountLabel || '',
                  pricingDescription: section.data?.pricing?.description || '',
                }}
                onSubmit={onSubmit}
                enableReinitialize
              >
                {(formik) => (
                  <Form>
                    <div className="middle_main_2 pt-4">
                      {/* Info Alert */}
                      <Alert variant="info" className="mb-4">
                        <i className="fa fa-info-circle me-2"></i>
                        <strong>Pro Tip:</strong> Changes will be reflected on
                        your live homepage once saved. Preview your changes
                        before publishing to ensure everything looks perfect.
                      </Alert>

                      {/* Basic Information Card */}
                      {(fields.title || fields.subtitle || fields.text) && (
                        <Card className="mb-4 shadow-sm border-0">
                          <Card.Header className="bg-primary bg-opacity-10 border-0">
                            <h5 className="mb-0">
                              <i className="fa fa-heading me-2 text-primary"></i>
                              Basic Information
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              {fields.title && (
                                <Col
                                  md={fields.subtitle ? 6 : 12}
                                  className="form-group mb-3"
                                >
                                  <label className="form-label">
                                    <i className="fa fa-font me-2 text-primary"></i>
                                    Title <span className="text-danger">*</span>
                                  </label>
                                  <CustomInput
                                    name="title"
                                    placeholder="Enter compelling title"
                                    className="form-control"
                                    isRequired
                                  />
                                  <small className="text-muted">
                                    Main heading displayed to users
                                  </small>
                                </Col>
                              )}

                              {fields.subtitle && (
                                <Col md={6} className="form-group mb-3">
                                  <label className="form-label">
                                    <i className="fa fa-text-height me-2 text-primary"></i>
                                    Subtitle
                                  </label>
                                  <CustomInput
                                    name="subtitle"
                                    placeholder="Supporting subtitle text"
                                    className="form-control"
                                  />
                                  <small className="text-muted">
                                    Secondary heading or tagline
                                  </small>
                                </Col>
                              )}

                              {fields.text && (
                                <Col md={12} className="form-group mb-3">
                                  <label className="form-label">
                                    <i className="fa fa-align-left me-2 text-primary"></i>
                                    Text Content{' '}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <CustomInput
                                    name="text"
                                    placeholder="Enter descriptive text"
                                    className="form-control"
                                    isRequired
                                  />
                                  <small className="text-muted">
                                    Main content text for this section
                                  </small>
                                </Col>
                              )}
                            </Row>
                          </Card.Body>
                        </Card>
                      )}

                      {/* Statistics Card */}
                      {(fields.count || fields.countLabel) && (
                        <Card className="mb-4 shadow-sm border-0">
                          <Card.Header className="bg-warning bg-opacity-10 border-0">
                            <h5 className="mb-0">
                              <i className="fa fa-chart-bar me-2 text-warning"></i>
                              Statistics Display
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              {fields.count && (
                                <Col md={6} className="form-group mb-3">
                                  <label className="form-label">
                                    <i className="fa fa-sort-numeric-up me-2 text-primary"></i>
                                    Count <span className="text-danger">*</span>
                                  </label>
                                  <CustomInput
                                    name="count"
                                    type="number"
                                    placeholder="50000"
                                    className="form-control"
                                    isRequired
                                  />
                                  <small className="text-muted">
                                    Numeric value to display
                                  </small>
                                </Col>
                              )}

                              {fields.countLabel && (
                                <Col md={6} className="form-group mb-3">
                                  <label className="form-label">
                                    <i className="fa fa-tag me-2 text-primary"></i>
                                    Count Label{' '}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <CustomInput
                                    name="countLabel"
                                    placeholder="members"
                                    className="form-control"
                                    isRequired
                                  />
                                  <small className="text-muted">
                                    Label describing the count (e.g.,
                                    &quot;members&quot;, &quot;customers&quot;)
                                  </small>
                                </Col>
                              )}
                            </Row>
                          </Card.Body>
                        </Card>
                      )}

                      {/* Perfect For List Card */}
                      {fields.perfectFor && (
                        <Card className="mb-4 shadow-sm border-0">
                          <Card.Header className="bg-success bg-opacity-10 border-0">
                            <h5 className="mb-0">
                              <i className="fa fa-users me-2 text-success"></i>
                              Perfect For
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <p className="text-muted mb-3">
                              <i className="fa fa-lightbulb me-2"></i>
                              Add target audience categories. Click &quot;Add
                              Item&quot; to include more entries.
                            </p>
                            <CustomFieldArray
                              name="perfectFor"
                              label="Audience Categories"
                              labelclassname="form-label"
                              className="form-control"
                              id="perfectFor"
                              values={formik.values.perfectFor}
                            />
                          </Card.Body>
                        </Card>
                      )}

                      {/* Features List Card */}
                      {fields.features && (
                        <Card className="mb-4 shadow-sm border-0">
                          <Card.Header className="bg-info bg-opacity-10 border-0">
                            <h5 className="mb-0">
                              <i className="fa fa-check-square me-2 text-info"></i>
                              Features & Benefits
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <p className="text-muted mb-3">
                              <i className="fa fa-lightbulb me-2"></i>
                              List key features or benefits. Each entry will
                              appear as a bullet point.
                            </p>
                            <CustomFieldArray
                              name="features"
                              label="Feature Items"
                              labelclassname="form-label"
                              className="form-control"
                              id="features"
                              values={formik.values.features}
                            />
                          </Card.Body>
                        </Card>
                      )}

                      {/* Pricing Card */}
                      {fields.pricing && (
                        <Card className="mb-4 shadow-sm border-0">
                          <Card.Header className="bg-success bg-opacity-10 border-0">
                            <h5 className="mb-0">
                              <i className="fa fa-dollar me-2 text-success"></i>
                              Pricing Details
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <p className="text-muted mb-3">
                              <i className="fa fa-info-circle me-2"></i>
                              Configure pricing display with original and
                              discounted prices
                            </p>
                            <Row>
                              <Col md={6} className="form-group mb-3">
                                <label className="form-label">
                                  <i className="fa fa-money me-2 text-primary"></i>
                                  Original Price
                                </label>
                                <CustomInput
                                  name="originalPrice"
                                  placeholder="$295"
                                  className="form-control"
                                />
                                <small className="text-muted">
                                  Full price before discount
                                </small>
                              </Col>
                              <Col md={6} className="form-group mb-3">
                                <label className="form-label">
                                  <i className="fa fa-tag me-2 text-primary"></i>
                                  Discounted Price
                                </label>
                                <CustomInput
                                  name="discountedPrice"
                                  placeholder="$195"
                                  className="form-control"
                                />
                                <small className="text-muted">
                                  Promotional price
                                </small>
                              </Col>
                              <Col md={6} className="form-group mb-3">
                                <label className="form-label">
                                  <i className="fa fa-percent me-2 text-primary"></i>
                                  Discount Label
                                </label>
                                <CustomInput
                                  name="discountLabel"
                                  placeholder="Save 33%"
                                  className="form-control"
                                />
                                <small className="text-muted">
                                  Badge text showing savings
                                </small>
                              </Col>
                              <Col md={6} className="form-group mb-3">
                                <label className="form-label">
                                  <i className="fa fa-info-circle me-2 text-primary"></i>
                                  Pricing Description
                                </label>
                                <CustomInput
                                  name="pricingDescription"
                                  placeholder="for the first month"
                                  className="form-control"
                                />
                                <small className="text-muted">
                                  Additional pricing context
                                </small>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      )}

                      {/* Call-to-Action Card */}
                      {(fields.ctaText || fields.ctaLink) && (
                        <Card className="mb-4 shadow-sm border-0">
                          <Card.Header className="bg-primary bg-opacity-10 border-0">
                            <h5 className="mb-0">
                              <i className="fa fa-hand-pointer me-2 text-primary"></i>
                              Call-to-Action Button
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              {fields.ctaText && (
                                <Col md={6} className="form-group mb-3">
                                  <label className="form-label">
                                    <i className="fa fa-mouse-pointer me-2 text-primary"></i>
                                    Button Text
                                  </label>
                                  <CustomInput
                                    name="ctaText"
                                    placeholder="Get Started"
                                    className="form-control"
                                  />
                                  <small className="text-muted">
                                    Text displayed on the button
                                  </small>
                                </Col>
                              )}

                              {fields.ctaLink && (
                                <Col md={6} className="form-group mb-3">
                                  <label className="form-label">
                                    <i className="fa fa-link me-2 text-primary"></i>
                                    Button Link
                                  </label>
                                  <CustomInput
                                    name="ctaLink"
                                    placeholder="/start"
                                    className="form-control"
                                  />
                                  <small className="text-muted">
                                    URL where button should navigate
                                  </small>
                                </Col>
                              )}
                            </Row>
                          </Card.Body>
                        </Card>
                      )}

                      {/* Image Upload Card */}
                      {fields.image && (
                        <Card className="mb-4 shadow-sm border-0">
                          <Card.Header className="bg-warning bg-opacity-10 border-0">
                            <h5 className="mb-0">
                              <i className="fa fa-image me-2 text-warning"></i>
                              Section Image
                            </h5>
                          </Card.Header>
                          <Card.Body>
                            <Alert variant="warning" className="mb-3">
                              <i className="fa fa-exclamation-triangle me-2"></i>
                              <strong>Image Requirements:</strong> Upload a
                              high-quality image (1000x1000px recommended) for
                              best results across all devices.
                            </Alert>
                            <ImageInput
                              label="Upload Image"
                              name="image"
                              id="section_image"
                              accept="image/*"
                              imagesLimit={1}
                              // imageDimention={{ width: 1000, height: 1000 }}
                            />
                          </Card.Body>
                        </Card>
                      )}

                      {/* Action Buttons */}
                      <Card className="border-0 bg-light">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-center">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={backToList}
                            >
                              <i className="fa fa-arrow-left me-2"></i>
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              variant="primary"
                              size="lg"
                              disabled={loading}
                              className="px-5"
                            >
                              {loading ? (
                                <>
                                  <i className="fa fa-spinner fa-spin me-2"></i>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-save me-2"></i>
                                  Save Changes
                                </>
                              )}
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName={section.sectionName}
      />
    </section>
  );
};

export default SingleSectionEditor;
